import {
  TripSortCriteria,
  AssetStatus,
  MaintenanceTaskStatus,
  GetOrgMaintenanceTasksQuery,
} from "../../../graphql/generated";
import { TableField } from "../../common/LynksTable/LynksTable";
import SmartLynksTable, {
  DeleteMutation,
  QueryFn,
} from "../../common/SmartLynksTable/SmartLynksTable";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  Tooltip,
} from "@mui/material";
import { useState } from "react";
import { useRoles } from "../../account/Access/RoleGuard";
import { capitalize, isNumber } from "lodash";
import { Check, Warning } from "@mui/icons-material";
import enumLabel from "../../../utils/labels/enumLabel";
import { formatDateTime } from "../../../utils/labels/formatDateTime";
import OrgMaintenanceTaskFormContainer from "../OrgMaintenanceTaskForm";
import { differenceInCalendarDays } from "date-fns";
import { testIds } from "./OrgMaintenanceTasksList.testIds";

type OrgMaintenanceTaskListItem =
  GetOrgMaintenanceTasksQuery["orgMaintenanceTasks"]["data"][0];

export type OrgMaintenanceTaskListProps = {
  query: QueryFn<
    OrgMaintenanceTaskListItem,
    "orgMaintenanceTasks",
    TripSortCriteria
  >;
  deleteMutation: DeleteMutation;
  onRefresh: () => void;
  onComplete: (orgMaintenanceTask: OrgMaintenanceTaskListItem) => void;
};

export default function OrgMaintenanceTasksList({
  query,
  deleteMutation,
  onRefresh,
  onComplete,
}: // onBulkCreate,
OrgMaintenanceTaskListProps) {
  // const { t } = useTranslation("users");
  const [formOpen, setFormOpen] = useState(false);
  const [selectedMaintenanceTask, setSelectedMaintenanceTask] =
    useState<OrgMaintenanceTaskListItem | null>(null);

  const roles = useRoles();

  const hasWriteAccess = roles.some((role) =>
    ["Carrier Admin", "Manager", "Dispatcher"].includes(role)
  );

  const fields: TableField<OrgMaintenanceTaskListItem, AssetStatus>[] = [
    {
      value: (task) => {
        const diffBetweenStartDateAndToday = differenceInCalendarDays(
          new Date(task.startDate),
          new Date()
        );

        return (
          task.status !== MaintenanceTaskStatus.Completed &&
          isNumber(task.alertBeforeDays) &&
          task.alertBeforeDays >= diffBetweenStartDateAndToday && (
            <Tooltip
              sx={{ alignItems: "center" }}
              title={`${task.label} expire in ${diffBetweenStartDateAndToday} days`}
            >
              <Warning color="error" />
            </Tooltip>
          )
        );
      },
      label: "",
      type: "custom",
    },
    {
      value: "label",
      label: "Label",
      type: "string",
    },
    {
      value: (maintenanceTask) => formatDateTime(maintenanceTask.startDate),
      label: "Start",
      type: "string",
    },
    {
      value: "endDate",
      label: "End",
      type: "datetime",
    },
    {
      value: (task) =>
        task.assignedToUser
          ? `${task.assignedToUser.firstName} ${task.assignedToUser.lastName}`
          : "N/A",
      label: "Assigned to",
      type: "string",
    },
    {
      value: (task) =>
        task.recurrence
          ? `${enumLabel(task.recurrence.frequency)} (${
              task.recurrence.interval
            })`
          : "",
      label: "Recurrence",
      type: "string",
    },
  ];

  const [status, setStatus] = useState<MaintenanceTaskStatus | null>(null);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Tabs data-testid={testIds.orgMaintenanceTasksListPendingNavigation}
          variant="standard"
          value={status}
          onChange={(event, value) => {
            setStatus(value);
          }}
        >
          <Tab data-testid={testIds.orgMaintenanceTasksListPendingView} label="Pending" value={null} />
          <Tab data-testid={testIds.orgMaintenanceTasksListCompleted} label="Completed" value={MaintenanceTaskStatus.Completed} />
        </Tabs>
        {/* <ExcelImporter
            mapping={maintenanceTasksMapping}
            schema={maintenanceTaskSchema}
            onBulkCreate={onBulkCreate}
          /> */}
        <Button data-testid={testIds.orgMaintenanceTasksListCreateTask}
          variant="contained"
          color="secondary"
          size="medium"
          id="new-maintenanceTask-button"
          onClick={() => {
            setFormOpen(true);
            setSelectedMaintenanceTask(null);
          }}
        >
          New Maintenance Task
        </Button>
      </Box>
      <SmartLynksTable
        query={query}
        dataKey="orgMaintenanceTasks"
        fields={fields}
        onRecordClick={(maintenanceTask) => {
          if (!hasWriteAccess) return;
          setFormOpen(true);
          setSelectedMaintenanceTask(maintenanceTask);
        }}
        disableSearch
        additionalQueryVariables={{
          status: status,
        }}
        deleteMutation={hasWriteAccess ? deleteMutation : undefined}
        actions={[
          {
            label: "Complete",
            icon: <Check />,
            tooltip: "Complete Maintenance Task",
            onClick: (maintenanceTask) => {
              onComplete(maintenanceTask);
            },
            isApplicable(record) {
              return record.status !== MaintenanceTaskStatus.Completed;
            },
          },
        ]}
      />
      <Dialog
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {capitalize(
            selectedMaintenanceTask
              ? "Edit Maintenance Task"
              : "New Maintenance Task"
          )}
        </DialogTitle>
        <DialogContent>
          {formOpen ? (
            <OrgMaintenanceTaskFormContainer
              orgMaintenanceTaskId={selectedMaintenanceTask?._id}
              onSaved={() => {
                setFormOpen(false);
                setSelectedMaintenanceTask(null);
                onRefresh();
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
